
import { getLocale } from "../i18n/index.js";

interface ApiErrorPayload {
  error?: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details: unknown;
  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

let csrfToken: string | undefined;

export async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetch("/api/csrf", { credentials: "same-origin" });
  if (!res.ok) throw new ApiError("csrf.unavailable", "CSRF unavailable", res.status);
  const data = (await res.json()) as { token: string };
  csrfToken = data.token;
  return csrfToken;
}

export function invalidateCsrf(): void {
  csrfToken = undefined;
}

interface ReqOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
}

export async function api<T = unknown>(path: string, opts: ReqOpts = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Accept-Language": getLocale(),
    ...opts.headers,
  };
  if (method !== "GET") headers["X-CSRF-Token"] = await ensureCsrf();

  const init: RequestInit = { method, credentials: "same-origin", headers };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.body);
  }

  const res = await fetch(path, init);
  if (res.status === 204) return undefined as T;

  let payload: unknown;
  try { payload = await res.json(); } catch { payload = null; }

  if (!res.ok) {
    const err = (payload as ApiErrorPayload)?.error;
    throw new ApiError(
      err?.code ?? "http." + res.status,
      err?.message ?? res.statusText,
      res.status,
      err?.details,
    );
  }
  return payload as T;
}
